import React, { useState, useEffect } from 'react';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import Modal from '../shared/Modal';
import { QuoteRecord, Application, FinanceProduct, InvoiceLineItem, FinanceAttachment } from '../../types';
import { auth, db } from '../../lib/firebase';
import {
  X, Plus, Trash2, DollarSign, Calculator, AlertCircle, Sparkles, Check, FileText, ChevronDown, Paperclip, Upload, Loader
} from 'lucide-react';
import { getSafeApplicantName } from '../../lib/nameUtils';
import { notifyToast } from '../../lib/toastStore';
import { workspaceHeader } from '../../lib/workspaces';
import SearchableSelect from '../shared/SearchableSelect';

const wsFetch = (url: string, options?: RequestInit): Promise<Response> => {
  return fetch(url, {
    ...options,
    headers: { ...workspaceHeader(), ...(options?.headers as Record<string, string>) },
  });
};

// Keep native select popups from blowing out the modal width — native selects
// size their dropdown list to the longest option, so over-long labels are tidied.
const tidy = (s: any, max = 56): string => {
  const t = String(s ?? '').trim();
  return t.length > max ? t.slice(0, max - 1).trimEnd() + '…' : t;
};
const applicantOption = (a: any): string =>
  `${tidy(getSafeApplicantName(a), 34)} — ${tidy(a.programName || a.programType || a.assignment?.programName || 'Program', 26)} (${tidy(a.userEmail || a.email || 'no email', 34)})`;


interface QuoteBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  quoteToEdit?: QuoteRecord | null;
  applications?: Application[];
  applicant?: Application;
  products: FinanceProduct[];
  preselectedApplicationId?: string;
  branding?: any;
  onQuoteSaved: () => void;
}

export default function QuoteBuilderModal({
  isOpen,
  onClose,
  quoteToEdit,
  applications = [],
  applicant,
  products,
  preselectedApplicationId,
  branding,
  onQuoteSaved,
}: QuoteBuilderModalProps) {
  const effectiveApplications = applications.length > 0 ? applications : (applicant ? [applicant] : []);
  useBodyScrollLock(isOpen);

  const [creationMode, setCreationMode] = useState<'prompt' | 'builder'>('prompt');
  const [selectedApplicantId, setSelectedApplicantId] = useState(preselectedApplicationId || '');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [expirationDate, setExpirationDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30); // default 30-day expiry
    return d.toISOString().slice(0, 10);
  });
  const [term, setTerm] = useState('');
  const [referrerName, setReferrerName] = useState('');
  const [sponsorName, setSponsorName] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [notes, setNotes] = useState('This is an official quote/estimate. Terms are valid until the expiration date.');
  const [terms, setTerms] = useState('Standard payment and program terms apply.');
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [productSearch, setProductSearch] = useState('');

  // Sequence state & catalogs
  const [programsList, setProgramsList] = useState<any[]>([]);
  const [sponsorsList, setSponsorsList] = useState<any[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [selectedVariableId, setSelectedVariableId] = useState('');
  const [selectedSponsorId, setSelectedSponsorId] = useState('');

  // Selected application object
  const currentApp = effectiveApplications.find(a => (a.applicationId || (a as any).id) === selectedApplicantId);

  useEffect(() => {
    if (!currentApp) return;
    const pId = currentApp.programId || currentApp.programType || '';
    const vId = (currentApp as any).pricingVariableId || (currentApp as any).pricingVariableSnapshot?.pricingVariableId || '';
    const sId = currentApp.sponsorId || (currentApp as any).assignedSponsor || (currentApp as any).sponsorName || '';
    setSelectedProgramId(pId);
    setSelectedVariableId(vId);
    setSelectedSponsorId(sId);
  }, [currentApp]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchCatalogs = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const [progRes, sponsRes, tmplRes] = await Promise.allSettled([
          wsFetch('/api/admin/programs', { headers: { Authorization: `Bearer ${token}` } }),
          wsFetch('/api/admin/sponsors', { headers: { Authorization: `Bearer ${token}` } }),
          wsFetch('/api/admin/finance/invoice-templates', { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        if (progRes.status === 'fulfilled' && progRes.value.ok) {
          const data = await progRes.value.json();
          setProgramsList(data.programs || []);
        }
        if (sponsRes.status === 'fulfilled' && sponsRes.value.ok) {
          const data = await sponsRes.value.json();
          setSponsorsList(data.sponsors || []);
        }
        if (tmplRes.status === 'fulfilled' && tmplRes.value.ok) {
          const data = await tmplRes.value.json();
          setTemplates((data.templates || []).filter((t: any) => {
            if (Array.isArray(t.types)) return t.types.includes('quote');
            return t.type === 'quote';
          }));
        }
      } catch (e) {
        console.error('Failed to fetch catalogs/templates for quote:', e);
      }
    };
    fetchCatalogs();
  }, [isOpen]);

  // Derived program object and available variables/sponsors
  const activeProgramObj = programsList.find(p => p.id === selectedProgramId || p.programId === selectedProgramId);
  const availableVariables = Array.isArray(activeProgramObj?.pricingVariables)
    ? activeProgramObj.pricingVariables.filter((v: any) => v.visibility === 'visible')
    : [];

  const availableAcdSponsors = React.useMemo(() => {
    if (!activeProgramObj || !selectedVariableId) return sponsorsList;
    const acds = Array.isArray(activeProgramObj.acdRecords) ? activeProgramObj.acdRecords : [];
    const activeVarAcds = acds.filter((a: any) => a.pricingVariableId === selectedVariableId && a.active !== false);
    if (activeVarAcds.length > 0) {
      const allowedSponsorIds = activeVarAcds.map((a: any) => a.sponsorId);
      return sponsorsList.filter(s => allowedSponsorIds.includes(s.sponsorId || s.id));
    }
    return sponsorsList;
  }, [activeProgramObj, selectedVariableId, sponsorsList]);

  // Filter templates based on Program -> Variable -> Sponsor ACD relationship
  const matchingTemplates = React.useMemo(() => {
    if (!activeProgramObj || !selectedVariableId || !selectedSponsorId) return [];
    const acds = Array.isArray(activeProgramObj.acdRecords) ? activeProgramObj.acdRecords : [];
    const matchedAcd = acds.find(
      (a: any) =>
        a.pricingVariableId === selectedVariableId &&
        a.sponsorId === selectedSponsorId &&
        a.active !== false
    );
    if (!matchedAcd || !matchedAcd.invoiceTemplateId) return [];
    const linkedTmplId = matchedAcd.invoiceTemplateId;
    return templates.filter(t => t.id === linkedTmplId && t.active !== false);
  }, [templates, activeProgramObj, selectedVariableId, selectedSponsorId]);

  const filteredProducts = products.filter(p =>
    !productSearch ||
    p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.description?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.productId?.toLowerCase().includes(productSearch.toLowerCase())
  );

  const [items, setItems] = useState<InvoiceLineItem[]>([
    {
      id: 'item_' + Math.random().toString(36).substring(2, 9),
      name: '',
      description: '',
      quantity: 1,
      rate: 0,
      discountType: 'fixed',
      discountValue: 0,
      discountAmount: 0,
      amount: 0,
      isCustom: true,
    },
  ]);

  const [discountValue, setDiscountValue] = useState<number>(0);
  const [taxPercent, setTaxPercent] = useState<number>(0);
  const [adjustment, setAdjustment] = useState<number>(0);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [attachments, setAttachments] = useState<FinanceAttachment[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (quoteToEdit) {
      setCreationMode('builder');
      setSelectedApplicantId(quoteToEdit.applicantId || '');
      setIssueDate(quoteToEdit.issueDate || new Date().toISOString().slice(0, 10));
      setExpirationDate(quoteToEdit.expirationDate || new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10));
      setTerm(quoteToEdit.term || '');
      setReferrerName(quoteToEdit.referrerName || '');
      setSponsorName(quoteToEdit.sponsorName || '');
      setItems(Array.isArray(quoteToEdit.items) && quoteToEdit.items.length > 0 ? quoteToEdit.items : items);
      setDiscountValue(Number(quoteToEdit.totalDiscount || quoteToEdit.discountValue || 0));
      setTaxPercent(Number(quoteToEdit.taxPercent || 0));
      setAdjustment(Number(quoteToEdit.adjustment || 0));
      setAdjustmentReason(quoteToEdit.adjustmentReason || '');
      setNotes(quoteToEdit.notes || notes);
      setTerms(quoteToEdit.terms || terms);
      setAttachments(Array.isArray(quoteToEdit.attachments) ? quoteToEdit.attachments : []);
    } else {
      setCreationMode('prompt');
      if (preselectedApplicationId) {
        handleApplicantChange(preselectedApplicationId);
      }
    }
  }, [isOpen, quoteToEdit, preselectedApplicationId]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchTemplates = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await wsFetch('/api/admin/finance/invoice-templates', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setTemplates(data.templates || []);
        }
      } catch (e) {
        console.error('Failed to fetch invoice templates:', e);
      }
    };
    fetchTemplates();
  }, [isOpen]);

  const handleApplyTemplate = (tmplId: string) => {
    setSelectedTemplateId(tmplId);
    if (!tmplId) return;
    const tmpl = templates.find(t => t.id === tmplId);
    if (!tmpl) return;

    if (Array.isArray(tmpl.items) && tmpl.items.length > 0) {
      const newItems: InvoiceLineItem[] = tmpl.items.map((it: any) => ({
        id: 'item_' + Math.random().toString(36).substring(2, 9),
        productId: it.productId || '',
        name: it.name || '',
        description: it.description || '',
        quantity: Number(it.quantity) || 1,
        rate: Number(it.rate) || 0,
        amount: (Number(it.quantity) || 1) * (Number(it.rate) || 0),
        isCustom: !it.productId,
      }));
      setItems(newItems);
    }
    if (tmpl.notes) setNotes(tmpl.notes);
    if (tmpl.terms) setTerms(tmpl.terms);
    if (tmpl.discountValue !== undefined) setDiscountValue(Number(tmpl.discountValue));
    if (tmpl.taxPercent !== undefined) setTaxPercent(Number(tmpl.taxPercent));
    notifyToast.success(`Template "${tmpl.name || tmpl.title}" applied!`);
  };

  const handleApplicantChange = (appId: string) => {
    setSelectedApplicantId(appId);
    const matched = effectiveApplications.find(a => (a as any).id === appId || a.applicationId === appId);
    if (matched) {
      setTerm(matched.term || matched.programTerm || matched.assignment?.programTerm || matched.programPreferences?.programTerm || '');
      setReferrerName(matched.referredByName || matched.referredBy || '');
      setSponsorName((matched as any).sponsorName || (matched.assignment as any)?.sponsorProviderId || '');
    }
  };

  const updateLineItem = (index: number, field: keyof InvoiceLineItem, value: any) => {
    const newItems = [...items];
    const item = { ...newItems[index], [field]: value };
    const qty = Number(item.quantity) || 1;
    const rate = Number(item.rate) || 0;
    item.amount = Math.round(qty * rate * 100) / 100;
    newItems[index] = item;
    setItems(newItems);
  };

  const addLineItem = (product?: FinanceProduct) => {
    if (product) {
      const newItem: InvoiceLineItem = {
        id: 'item_' + Math.random().toString(36).substring(2, 9),
        productId: product.productId,
        name: product.name,
        description: product.description || '',
        quantity: 1,
        rate: (product as any).price || (product as any).sellingPrice || 0,
        discountAmount: 0,
        amount: (product as any).price || (product as any).sellingPrice || 0,
        isCustom: false,
      };
      setItems([...items, newItem]);
    } else {
      const newItem: InvoiceLineItem = {
        id: 'item_' + Math.random().toString(36).substring(2, 9),
        name: '',
        description: '',
        quantity: 1,
        rate: 0,
        discountAmount: 0,
        amount: 0,
        isCustom: true,
      };
      setItems([...items, newItem]);
    }
  };

  const removeLineItem = (index: number) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const formData = new FormData();
      Array.from(files).forEach(f => formData.append('files', f));

      const res = await wsFetch('/api/admin/finance/upload-attachment', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!res.ok) throw new Error('Failed to upload attachment');
      const data = await res.json();
      setAttachments(prev => [...prev, ...(data.files || [])]);
      notifyToast.success('File attachments added');
    } catch (err: any) {
      notifyToast.error(err.message || 'File upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (attId: string) => {
    setAttachments(prev => prev.filter(a => a.id !== attId));
  };

  const subtotal = Math.round(items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) * 100) / 100;
  const taxTotal = Math.round((subtotal - discountValue) * (taxPercent / 100) * 100) / 100;
  const total = Math.max(0, Math.round((subtotal - discountValue + taxTotal + adjustment) * 100) / 100);

  const handleSaveQuote = async (status: 'Draft' | 'Sent') => {
    if (!selectedApplicantId) {
      setError('Please select an applicant.');
      return;
    }
    if (items.length === 0 || !items[0].name.trim()) {
      setError('Please add at least one line item name.');
      return;
    }

    setError('');
    setSaving(true);
    try {
      const matched = effectiveApplications.find(a => (a as any).id === selectedApplicantId || a.applicationId === selectedApplicantId);
      const token = await auth.currentUser?.getIdToken();

      const payload = {
        ...(quoteToEdit ? { quoteId: quoteToEdit.quoteId, id: quoteToEdit.id, quoteNumber: quoteToEdit.quoteNumber } : {}),
        applicantId: (matched as any)?.id || matched?.applicationId || selectedApplicantId,
        applicantUserId: matched?.userId || '',
        applicantName: matched ? getSafeApplicantName(matched) : 'Applicant',
        applicantEmail: matched?.userEmail || (matched as any)?.email || '',
        programId: (matched as any)?.programId || (matched as any)?.assignment?.programId || '',
        programName: (matched as any)?.programName || (matched as any)?.assignment?.programName || matched?.programType || '',
        term,
        referrerName,
        sponsorName,
        issueDate,
        expirationDate,
        items,
        subtotal,
        totalDiscount: discountValue,
        taxPercent,
        taxTotal,
        adjustment,
        adjustmentReason,
        total,
        currency,
        status,
        notes,
        terms,
        attachments,
      };

      const res = await wsFetch('/api/admin/finance/quotes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to save quote');
      }

      notifyToast.success(status === 'Sent' ? 'Quote saved & marked as Sent' : 'Quote saved as Draft');
      onQuoteSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred while saving the quote');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal onClose={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden text-slate-800 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50/50">
          <div>
            <h3 className="text-base font-extrabold text-slate-900 font-display">
              {quoteToEdit ? `Edit Draft Quote #${quoteToEdit.quoteNumber}` : 'Create New Quote / Estimate'}
            </h3>
            <p className="text-caption text-slate-500">
              {quoteToEdit ? 'Update line items, discount, tax, and terms before sending.' : 'Select a pre-configured template or build a custom quote manually.'}
            </p>
          </div>
          <button aria-label="Close" title="Close" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all cursor-pointer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Prompt Mode */}
        {creationMode === 'prompt' && !quoteToEdit ? (
          <div className="p-8 space-y-6 overflow-y-auto flex-1 text-xs">
            <div className="text-center max-w-md mx-auto space-y-2">
              <div className="h-12 w-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto">
                <FileText className="h-6 w-6" />
              </div>
              <h3 className="text-base font-black text-slate-900">How would you like to create this quote?</h3>
              <p className="text-slate-500 text-xs">
                Select from pre-configured workspace templates in Finance Management or start with a custom blank quote.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto pt-2">
              <div className="bg-slate-50 hover:bg-slate-100/80 border-2 border-blue-200 hover:border-blue-400 p-5 rounded-2xl transition-all flex flex-col justify-between space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-blue-700 font-black text-sm">
                    <Sparkles className="h-4 w-4" />
                    <span>Select from Template(s)</span>
                  </div>
                  <p className="text-slate-500 text-[11px] leading-relaxed">
                    Choose from standard program fee packages, visa fees, and fee schedules saved in Finance Management.
                  </p>

                  <div className="pt-2 space-y-3">
                    {/* Applicant Selector */}
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-600 block">Select Applicant *</label>
                      <select
                        value={selectedApplicantId}
                        onChange={(e) => handleApplicantChange(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-xl p-2 text-xs font-semibold text-slate-800 outline-none focus:border-brand"
                      >
                        <option value="">-- Choose Applicant --</option>
                        {effectiveApplications.map((app) => {
                  const aId = app.applicationId || (app as any).id;
                          return (
                            <option key={aId} value={aId}>
                              {applicantOption(app)}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {selectedApplicantId && (
                      <div className="space-y-2 p-3 bg-white rounded-xl border border-slate-200">
                        <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                          Assignment Sequence (Program → Variable → Sponsor)
                        </div>

                        {/* Program Picker */}
                        {!selectedProgramId ? (
                          <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-[11px]">
                            <strong>Program Missing:</strong> Select a program for this applicant to view available pricing variables.
                            <select
                              value={selectedProgramId}
                              onChange={(e) => {
                                setSelectedProgramId(e.target.value);
                                setSelectedVariableId('');
                                setSelectedSponsorId('');
                              }}
                              className="w-full mt-1.5 bg-white border border-amber-300 rounded p-1 text-xs font-semibold text-slate-800"
                            >
                              <option value="">-- Assign Program --</option>
                              {programsList.map((p) => (
                                <option key={p.id || p.programId} value={p.id || p.programId}>
                                  {p.name} ({p.code || 'No code'})
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : !selectedVariableId ? (
                          <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-[11px]">
                            <strong>Pricing Variable Missing:</strong> Select a pricing variable for <strong>{activeProgramObj?.name || 'Program'}</strong>.
                            <select
                              value={selectedVariableId}
                              onChange={(e) => {
                                setSelectedVariableId(e.target.value);
                                setSelectedSponsorId('');
                              }}
                              className="w-full mt-1.5 bg-white border border-amber-300 rounded p-1 text-xs font-semibold text-slate-800"
                            >
                              <option value="">-- Assign Pricing Variable --</option>
                              {availableVariables.map((v: any) => (
                                <option key={v.id} value={v.id}>
                                  {v.name} ({v.code || 'No code'})
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : !selectedSponsorId ? (
                          <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-[11px]">
                            <strong>Sponsor/Partner Missing:</strong> Assign an eligible sponsor with an active ACD relationship.
                            <select
                              value={selectedSponsorId}
                              onChange={(e) => setSelectedSponsorId(e.target.value)}
                              className="w-full mt-1.5 bg-white border border-amber-300 rounded p-1 text-xs font-semibold text-slate-800"
                            >
                              <option value="">-- Assign Partner / Sponsor --</option>
                              {availableAcdSponsors.map((s: any) => {
                                const sId = s.sponsorId || s.id;
                                const sName = s.name || s.companyName || s.displayName || sId;
                                return (
                                  <option key={sId} value={sId}>
                                    {sName} {s.code ? `(${s.code})` : ''}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        ) : (
                          <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-[11px] text-emerald-800 font-medium">
                            <Check className="w-3.5 h-3.5 inline text-emerald-600 mr-1" />
                            <strong>Assigned Sequence Complete:</strong> {activeProgramObj?.name} &bull; {availableVariables.find((v: any) => v.id === selectedVariableId)?.name} &bull; {sponsorsList.find(s => (s.sponsorId || s.id) === selectedSponsorId)?.name || 'Sponsor'}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Matching Templates List */}
                    <label className="text-[10px] font-extrabold uppercase text-slate-400 tracking-wider block">
                      Matching Quote Templates ({matchingTemplates.length})
                    </label>

                    {!selectedProgramId || !selectedVariableId || !selectedSponsorId ? (
                      <p className="text-[11px] text-slate-500 italic bg-white p-3 rounded-xl border border-slate-200 text-center">
                        Complete the Program → Variable → Sponsor assignment above to unlock available quote templates.
                      </p>
                    ) : matchingTemplates.length > 0 ? (
                      <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                        {matchingTemplates.map(tmpl => (
                          <button
                            key={tmpl.id}
                            type="button"
                            onClick={() => {
                              handleApplyTemplate(tmpl.id);
                              setCreationMode('builder');
                            }}
                            className="w-full text-left p-2.5 bg-white border border-slate-200 hover:border-blue-400 hover:bg-blue-50/50 rounded-xl transition-all cursor-pointer group"
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-extrabold text-slate-800 text-xs group-hover:text-blue-700">
                                {tmpl.code ? `[${tmpl.code}] ` : ''}{tmpl.name || tmpl.title}
                              </span>
                              <span className="text-[10px] font-bold text-slate-400 font-mono">Apply &rarr;</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-amber-700 italic bg-amber-50 p-3 rounded-xl border border-amber-200 text-center">
                        No matching quote template configured for this exact Program, Variable, and Sponsor combination. Configure a matching quote template in Workspace Settings or click "Build Custom Quote" below.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 hover:bg-slate-100/80 border-2 border-slate-200 hover:border-slate-300 p-5 rounded-2xl transition-all flex flex-col justify-between space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-slate-800 font-black text-sm">
                    <Calculator className="h-4 w-4 text-slate-600" />
                    <span>Custom Blank Quote</span>
                  </div>
                  <p className="text-slate-500 text-[11px] leading-relaxed">
                    Build a custom quote line-by-line using catalog products or manually entered custom amounts.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setCreationMode('builder')}
                  className="w-full py-2.5 bg-brand hover:bg-brand-active text-white rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center space-x-1"
                >
                  <span>Build Custom Quote</span>
                  <span>&rarr;</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Builder Mode */
          <div className="p-6 space-y-6 overflow-y-auto flex-1 text-xs">
            {error && (
              <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 font-bold flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />
                <span>{error}</span>
              </div>
            )}

            {/* Applicant & General Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50/70 p-4 rounded-2xl border border-slate-200">
              <div className="md:col-span-3">
                <label className="text-caption font-extrabold uppercase text-slate-400 tracking-wider block mb-1">Target Applicant *</label>
                <SearchableSelect
                  value={selectedApplicantId}
                  onChange={(val) => handleApplicantChange(val)}
                  options={effectiveApplications.map(app => ({
                    value: (app as any).id || app.applicationId,
                    label: getSafeApplicantName(app),
                    hint: `${app.applicationId || ''} · ${app.userEmail || (app as any).email || ''}`.trim(),
                    tag: (app as any).programName || app.programType || undefined
                  }))}
                  placeholder="Search and select target applicant..."
                  allowClear={true}
                />
              </div>

              <div>
                <label className="text-caption font-extrabold uppercase text-slate-400 tracking-wider block mb-1">Issue Date</label>
                <input
                  type="date"
                  className="w-full bg-white border border-slate-200 text-xs font-bold p-2.5 rounded-xl text-slate-800 outline-none"
                  value={issueDate}
                  onChange={e => setIssueDate(e.target.value)}
                />
              </div>

              <div>
                <label className="text-caption font-extrabold uppercase text-slate-400 tracking-wider block mb-1">Expiration Date</label>
                <input
                  type="date"
                  className="w-full bg-white border border-slate-200 text-xs font-bold p-2.5 rounded-xl text-slate-800 outline-none"
                  value={expirationDate}
                  onChange={e => setExpirationDate(e.target.value)}
                />
              </div>

              <div>
                <label className="text-caption font-extrabold uppercase text-slate-400 tracking-wider block mb-1">Program Term</label>
                <input
                  type="text"
                  placeholder="e.g. Spring 2025"
                  className="w-full bg-white border border-slate-200 text-xs font-bold p-2.5 rounded-xl text-slate-800 outline-none"
                  value={term}
                  onChange={e => setTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Searchable Catalog Product Dropdown */}
            {products.length > 0 && (
              <div className="space-y-1.5 bg-slate-50 border border-slate-200 p-3 rounded-2xl">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-black uppercase text-slate-500 tracking-wider block">Add Product from Workspace Catalog</label>
                  <input
                    type="text"
                    placeholder="Search catalog..."
                    value={productSearch}
                    onChange={e => setProductSearch(e.target.value)}
                    className="bg-white border border-slate-200 rounded-lg px-2 py-0.5 text-xs font-medium text-slate-700 outline-none w-44"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <SearchableSelect
                      value=""
                      onChange={v => {
                        if (!v) return;
                        const found = products.find(p => p.id === v || p.productId === v);
                        if (found) addLineItem(found);
                      }}
                      options={filteredProducts.map(prod => ({
                        value: prod.id || prod.productId,
                        label: `${tidy(prod.name, 44)} — $${Number(prod.price || prod.sellingPrice || 0).toFixed(2)} USD (${tidy(prod.productId, 22)})`,
                      }))}
                      placeholder={`-- Select Product from Catalogue (${filteredProducts.length} Products) --`}
                      allowClear={false}
                      emptyLabel="No products found"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Items Table */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-caption font-extrabold uppercase text-slate-400 tracking-wider">Line Items</span>
                <button
                  type="button"
                  onClick={() => addLineItem()}
                  className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center space-x-1 cursor-pointer"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Add Custom Item</span>
                </button>
              </div>

              <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase font-black tracking-wider text-slate-400">
                    <tr>
                      <th className="p-3">Item Name & Details</th>
                      <th className="p-3 w-20 text-center">Qty</th>
                      <th className="p-3 w-28 text-right">Rate ($)</th>
                      <th className="p-3 w-28 text-right">Amount ($)</th>
                      <th className="p-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium text-xs">
                    {items.map((item, index) => (
                      <tr key={item.id || index} className="hover:bg-slate-50/50">
                        <td className="p-3 space-y-1">
                          <input
                            type="text"
                            placeholder="Item name / Title *"
                            className="w-full bg-white border border-slate-200 rounded-lg p-1.5 text-xs font-bold text-slate-900 outline-none"
                            value={item.name}
                            onChange={e => updateLineItem(index, 'name', e.target.value)}
                          />
                          <textarea
                            rows={3}
                            placeholder="Description (optional)"
                            className="w-full bg-white border border-slate-200 rounded-lg p-1.5 text-[11px] text-slate-500 outline-none resize-y"
                            value={item.description || ''}
                            onChange={e => updateLineItem(index, 'description', e.target.value)}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            min="1"
                            className="w-full bg-white border border-slate-200 rounded-lg p-1.5 text-center text-xs font-bold text-slate-900 outline-none"
                            value={item.quantity}
                            onChange={e => updateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="w-full bg-white border border-slate-200 rounded-lg p-1.5 text-right font-mono text-xs font-bold text-slate-900 outline-none"
                            value={item.rate}
                            onChange={e => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)}
                          />
                        </td>
                        <td className="p-3 text-right font-mono font-bold text-slate-900">
                          ${Number(item.amount || 0).toFixed(2)}
                        </td>
                        <td className="p-3 text-center">
                          <button aria-label="Remove line item" title="Remove line item"
                            type="button"
                            onClick={() => removeLineItem(index)}
                            disabled={items.length === 1}
                            className="text-slate-300 hover:text-red-500 disabled:opacity-30 cursor-pointer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Calculations & Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50/70 p-4 rounded-2xl border border-slate-200">
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-extrabold uppercase text-slate-400 tracking-wider block mb-1">Quote Customer Notes</label>
                  <textarea
                    rows={4}
                    className="w-full bg-white border border-slate-200 rounded-xl p-2.5 text-xs text-slate-700 outline-none resize-y"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-extrabold uppercase text-slate-400 tracking-wider block mb-1">Terms & Conditions</label>
                  <textarea
                    rows={4}
                    className="w-full bg-white border border-slate-200 rounded-xl p-2.5 text-xs text-slate-700 outline-none resize-y"
                    value={terms}
                    onChange={e => setTerms(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2 text-xs font-semibold text-slate-600">
                <div className="flex justify-between py-1 border-b border-slate-200">
                  <span>Subtotal</span>
                  <span className="font-mono font-bold text-slate-900">${subtotal.toFixed(2)}</span>
                </div>

                <div className="flex justify-between items-center py-1 border-b border-slate-200">
                  <span>Discount ($)</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-24 px-2 py-1 bg-white border border-slate-200 rounded-lg text-right font-mono font-bold text-slate-900 outline-none"
                    value={discountValue}
                    onChange={e => setDiscountValue(parseFloat(e.target.value) || 0)}
                  />
                </div>

                <div className="flex justify-between items-center py-1 border-b border-slate-200">
                  <span>Tax (%)</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="w-24 px-2 py-1 bg-white border border-slate-200 rounded-lg text-right font-mono font-bold text-slate-900 outline-none"
                    value={taxPercent}
                    onChange={e => setTaxPercent(parseFloat(e.target.value) || 0)}
                  />
                </div>

                <div className="flex justify-between items-center py-1 border-b border-slate-200">
                  <span>Adjustment ($)</span>
                  <input
                    type="number"
                    step="0.01"
                    className="w-24 px-2 py-1 bg-white border border-slate-200 rounded-lg text-right font-mono font-bold text-slate-900 outline-none"
                    value={adjustment}
                    onChange={e => setAdjustment(parseFloat(e.target.value) || 0)}
                  />
                </div>

                <div className="flex justify-between py-2 text-sm font-black text-slate-900 border-t-2 border-slate-900">
                  <span>Total Amount</span>
                  <span className="font-mono text-blue-600">${total.toFixed(2)} USD</span>
                </div>
              </div>
            </div>

            {/* Attachments Section */}
            <div className="space-y-3 pt-2">
              <div className="flex justify-between items-center">
                <span className="text-caption font-extrabold uppercase text-slate-400 tracking-wider">Multi-File Attachments ({attachments.length})</span>
                <label className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl cursor-pointer flex items-center space-x-1 transition-all">
                  <Upload className="h-3.5 w-3.5 text-slate-500" />
                  <span>Upload Files</span>
                  <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" onChange={handleFileUpload} className="hidden" disabled={uploading} />
                </label>
              </div>

              {uploading && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-700 font-bold flex items-center space-x-2 text-xs">
                  <Loader className="h-4 w-4 animate-spin text-blue-500" />
                  <span>Uploading attachments...</span>
                </div>
              )}

              {attachments.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {attachments.map(att => (
                    <div key={att.id} className="p-2.5 bg-white border border-slate-200 rounded-xl flex items-center justify-between text-xs">
                      <div className="flex items-center space-x-2 truncate">
                        <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                        <span className="font-bold text-slate-800 truncate">{att.fileName}</span>
                      </div>
                      <button aria-label="Remove attachment" title="Remove attachment" type="button" onClick={() => removeAttachment(att.id)} className="text-slate-400 hover:text-red-600 p-1">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/50 flex justify-between items-center">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-50 transition-all cursor-pointer"
          >
            Cancel
          </button>

          <div className="flex items-center space-x-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => handleSaveQuote('Draft')}
              className="px-4 py-2 bg-brand hover:bg-brand-active text-white text-xs font-bold rounded-xl transition-all cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save as Draft'}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => handleSaveQuote('Sent')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl transition-all cursor-pointer shadow-sm disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save & Mark Sent'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
